/**
 * Email accounts in the app (2026-10-28, docs/decisions/2026-10-28-email-accounts.md):
 * - the banner until the email is verified ("Resend", "Change email",
 *   "Enter code"); the app works meanwhile, as on Discord;
 * - "Add your email", a step that can't be skipped, for an account made
 *   before email sign-in (it signed in by username);
 * - Settings › Security › Email: the address masked (an eye shows it), its
 *   state, a pending change, and "Change";
 * - the links in emails (`?verify_email=`, `?reset_password=`), read once and
 *   taken off the address bar.
 * The server enforces every rule; this only says what happened, plainly.
 */
import {
  ApiErrorCode,
  FieldErrorCode,
  maskEmail,
  validateEmail,
  type PrivateUser,
} from '@neosocial/protocol';
import { useState, type FormEvent, type ReactNode } from 'react';
import { create } from 'zustand';
import { tr, useT } from '../i18n';
import { api, ApiRequestError, isApiRequestError } from '../lib/api';
import { errorText } from '../auth/Auth';
import { forgetLinkParam, takeLinkParam } from '../auth/links';
import { SetGroup, SetRow } from '../settings/Settings';
import { updateData, useData } from '../store/data';
import { signOut } from '../store/session';
import { Btn, Icon, IconBtn } from '../ui/kit';
import { Dialog, toast } from '../ui/overlay';
import { PasswordInput } from '../auth/PasswordInput';

/* ------------------------------ links in emails ------------------------------ */

/**
 * The verification link: works signed in or out. Says the result in a toast;
 * when signed in, the server's USER_UPDATE refreshes the account too.
 */
export async function verifyLinkOnLoad(): Promise<void> {
  const token = takeLinkParam('verify_email');
  if (!token) return;
  // once per link: a second call (React's StrictMode runs effects twice) must not spend it again
  forgetLinkParam('verify_email');
  const { t } = tr();
  try {
    const r = await api.auth.verifyEmailLink({ token });
    toast(t(r.result === 'changed' ? 'email.link.changed' : 'email.link.verified'), {
      icon: 'check-circle',
      tone: 'ok',
      duration: 6000,
    });
  } catch (e) {
    toast(errorText(e, t), { icon: 'alert', tone: 'danger', duration: 8000 });
  }
}

/* ------------------------------ shared bits ------------------------------ */

type EmailDialog = 'verify' | 'change' | null;
export const useEmailUi = create<{ dialog: EmailDialog }>(() => ({ dialog: null }));
export const openEmailDialog = (dialog: EmailDialog) => useEmailUi.setState({ dialog });

function setMe(u: PrivateUser): void {
  updateData((d) => (d.me && d.me.id === u.id ? { ...d, me: { ...d.me, ...u } } : d));
}

/** Until when the email can't be added or changed (7 days after a recovery), or null. */
export function emailLockedUntil(me: PrivateUser | null, now = Date.now()): string | null {
  const until = me?.nx_email_locked_until;
  return until && Date.parse(until) > now ? until : null;
}

/** Where a code was sent: the pending new address, else the account's own. */
function codeAddress(me: PrivateUser | null): string | null {
  return me?.nx_pending_email ?? me?.email ?? null;
}

/** `a***@g***.com`, with an eye to show it in full (Discord's way). */
export function MaskedEmail({ email }: { email: string }) {
  const { t } = useT();
  const [shown, setShown] = useState(false);
  return (
    <span className="ns-email">
      <bdi dir="ltr" className="ns-email-v" data-masked={!shown || undefined}>
        {shown ? email : maskEmail(email)}
      </bdi>
      <IconBtn
        icon={shown ? 'cm-eye-off' : 'eye'}
        label={t(shown ? 'email.hide' : 'email.show')}
        pressed={shown}
        onClick={() => setShown(!shown)}
      />
    </span>
  );
}

function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string | null;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="ns-field">
      <span className="ns-label">{label}</span>
      {children}
      {error ? (
        <span className="ns-err" role="alert">
          {error}
        </span>
      ) : help ? (
        <span className="ns-help">{help}</span>
      ) : null}
    </label>
  );
}

/** Field errors of the email routes, in calm words; anything else as one message. */
function emailErrors(e: unknown): Record<string, string> {
  const { t } = tr();
  if (e instanceof ApiRequestError) {
    const f = e.fieldCodes();
    const out: Record<string, string> = {};
    // you are signed in here: the sign-up wording ("Sign in, or Forgot your password?") doesn't fit
    if (f.email === FieldErrorCode.EMAIL_TAKEN) out.email = t('set.email.taken');
    else if (f.email === FieldErrorCode.EMAIL_UNCHANGED) out.email = t('auth.email.unchanged');
    else if (f.email) out.email = t('auth.email.invalid');
    if (e.code === ApiErrorCode.INVALID_CREDENTIALS) out.password = t('ns.pw.wrong');
    else if (f.password) out.password = t('email.add.password');
    if (e.code === ApiErrorCode.INVALID_MFA_CODE) out.code = t('auth.mfa.wrong');
    else if (e.code === ApiErrorCode.SECOND_FACTOR_REQUIRED) out.code = t('ns.err.100007');
    if (Object.keys(out).length) return out;
  }
  return { form: errorText(e, t) };
}

/* ------------------------------ entering the code ------------------------------ */

/**
 * The 6-digit code for the unverified email or the pending change, with
 * "Resend" and "Change email". `onDone` after a verified code.
 */
export function VerifyCodeForm({
  onDone,
  onChange,
  later,
}: {
  onDone(u: PrivateUser): void;
  onChange?: () => void;
  later?: () => void;
}) {
  const { t } = useT();
  const me = useData((d) => d.me);
  const address = codeAddress(me);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<'verify' | 'resend' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const digits = code.replace(/[\s-]/g, '');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (digits.length !== 6) return;
    setBusy('verify');
    setErr(null);
    try {
      const u = await api.me.verifyEmail({ code: digits });
      setMe(u);
      onDone(u);
    } catch (er) {
      setErr(errorText(er, tr().t));
    } finally {
      setBusy(null);
    }
  };
  const resend = async () => {
    setBusy('resend');
    setErr(null);
    try {
      await api.me.resendEmail();
      setCode('');
      toast(t('email.resent', { email: maskEmail(address ?? '') }), { icon: 'mail', tone: 'ok' });
    } catch (er) {
      setErr(errorText(er, tr().t));
    } finally {
      setBusy(null);
    }
  };
  return (
    <form className="ns-form ns-email-form" onSubmit={submit} noValidate>
      <p className="ns-help">{t('email.verify.body', { email: maskEmail(address ?? '') })}</p>
      <Field label={t('email.verify.code')} error={err}>
        <input
          className="nx-input ns-code-in"
          dir="ltr"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={9}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-invalid={!!err || undefined}
          data-autofocus
        />
      </Field>
      <div className="ns-row-btns">
        <Btn size="sm" icon="mail" busy={busy === 'resend'} disabled={!!busy} onClick={resend}>
          {t('email.resend')}
        </Btn>
        {onChange ? (
          <Btn size="sm" icon="edit" disabled={!!busy} onClick={onChange}>
            {t('email.change')}
          </Btn>
        ) : null}
      </div>
      <div className="ns-row-btns end">
        {later ? (
          <Btn size="sm" kind="quiet" onClick={later}>
            {t('email.later')}
          </Btn>
        ) : null}
        <Btn
          kind="primary"
          size="sm"
          type="submit"
          disabled={digits.length !== 6 || !!busy}
          busy={busy === 'verify'}
        >
          {t('email.verify.submit')}
        </Btn>
      </div>
    </form>
  );
}

function doneToast(before: PrivateUser | null, after: PrivateUser): void {
  const { t } = tr();
  const changed = !!before?.nx_pending_email && after.email === before.nx_pending_email;
  toast(
    changed
      ? t('email.verify.changed', { email: maskEmail(after.email ?? '') })
      : t('email.link.verified'),
    { icon: 'check-circle', tone: 'ok' },
  );
}

/* ------------------------------ changing it ------------------------------ */

/**
 * A new address: needs the password (and, for a verified email with two-step
 * on, a two-step code). A verified email changes only after the new one's
 * code; an unverified one is replaced at once. `onSent` when a code went out.
 */
export function ChangeEmailForm({
  onSent,
  onCancel,
  intro,
  note,
  askPassword = true,
  submitLabel,
}: {
  onSent(u: PrivateUser): void;
  onCancel?: () => void;
  intro?: string;
  /** Said under the fields, before the buttons. */
  note?: string;
  /** False for the "Add your email" step right after signing in (the server asks if it must). */
  askPassword?: boolean;
  submitLabel?: string;
}) {
  const { t } = useT();
  const me = useData((d) => d.me);
  const verified = !!me?.verified;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needPassword, setNeedPassword] = useState(askPassword || verified);
  // With two-step on, the server also wants its code (a two-step sign-in in
  // the last 10 minutes counts, so "Add your email" right after one doesn't ask).
  const [askedCode, setAskedCode] = useState(false);
  const [touched, setTouched] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const check = validateEmail(email);
  const emailMsg = errs.email || (touched && email && !check.ok ? t('auth.email.invalid') : null);
  const needCode = (verified && !!me?.mfa_enabled) || askedCode;
  const ready = check.ok && (!needPassword || password) && (!needCode || code.trim());
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!ready) return;
    setBusy(true);
    setErrs({});
    try {
      const u = await api.me.setEmail({
        email: check.value,
        ...(needPassword ? { password } : {}),
        ...(needCode ? { code: code.trim() } : {}),
      });
      setMe(u);
      onSent(u);
    } catch (er) {
      // the sign-in is no longer fresh: ask for the password, keep what was typed
      if (isApiRequestError(er, ApiErrorCode.STEP_UP_REQUIRED)) {
        setNeedPassword(true);
        setErrs({ password: t('email.add.password') });
      } else if (isApiRequestError(er, ApiErrorCode.SECOND_FACTOR_REQUIRED) && !needCode) {
        // two-step is on: the code field appears, the rest stays typed
        setAskedCode(true);
        setErrs({ code: t('email.add.code') });
      } else {
        // locked since the account was loaded (a recovery elsewhere): the app learns until when
        const until = isApiRequestError(er, ApiErrorCode.CONTACTS_LOCKED)
          ? (er as ApiRequestError).body?.nx_until
          : undefined;
        if (until) {
          updateData((d) => (d.me ? { ...d, me: { ...d.me, nx_email_locked_until: until } } : d));
        }
        setErrs(emailErrors(er));
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="ns-form ns-email-form" onSubmit={submit} noValidate>
      {intro ? <p className="ns-help">{intro}</p> : null}
      <Field label={t('email.new')} error={emailMsg}>
        <input
          className="nx-input"
          type="email"
          dir="ltr"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          onBlur={() => setTouched(true)}
          onChange={(e) => (setEmail(e.target.value), setErrs((x) => ({ ...x, email: '' })))}
          aria-invalid={!!emailMsg || undefined}
          data-autofocus
        />
      </Field>
      {needPassword ? (
        <Field label={t('auth.password.label')} error={errs.password}>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(e) => (
              setPassword(e.target.value),
              setErrs((x) => ({ ...x, password: '' }))
            )}
            aria-invalid={!!errs.password || undefined}
          />
        </Field>
      ) : null}
      {needCode ? (
        <Field label={t('ns.mfa.codeOrRecovery')} error={errs.code}>
          <input
            className="nx-input ns-code-in"
            dir="ltr"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => (setCode(e.target.value), setErrs((x) => ({ ...x, code: '' })))}
            aria-invalid={!!errs.code || undefined}
          />
        </Field>
      ) : null}
      {note ? <p className="ns-help">{note}</p> : null}
      {errs.form ? (
        <p className="ns-err ns-form-err" role="alert">
          {errs.form}
        </p>
      ) : null}
      <div className="ns-row-btns end">
        {onCancel ? (
          <Btn size="sm" onClick={onCancel}>
            {t('c.cancel')}
          </Btn>
        ) : null}
        <Btn kind="primary" size="sm" type="submit" disabled={!ready || busy} busy={busy}>
          {submitLabel ?? t('email.changeSubmit')}
        </Btn>
      </div>
    </form>
  );
}

/* ------------------------------ the dialogs ------------------------------ */

/** "Enter the code" and "Change your email", opened from the banner or Settings. */
export function EmailDialogs() {
  const { t } = useT();
  const dialog = useEmailUi((s) => s.dialog);
  const me = useData((d) => d.me);
  const close = () => openEmailDialog(null);
  if (!dialog || !me) return null;
  if (dialog === 'verify') {
    return (
      <Dialog title={t('email.verify.title')} icon="mail" tone="mode" onClose={close} width={460}>
        <VerifyCodeForm
          onDone={(u) => (doneToast(me, u), close())}
          onChange={() => openEmailDialog('change')}
        />
      </Dialog>
    );
  }
  return (
    <Dialog title={t('email.changeTitle')} icon="mail" tone="mode" onClose={close} width={460}>
      <ChangeEmailForm
        intro={t(me.verified ? 'email.changeBody' : 'email.changeBodyUnverified')}
        onCancel={close}
        onSent={() => openEmailDialog('verify')}
      />
    </Dialog>
  );
}

/* ------------------------------ the banner ------------------------------ */

/** Until the email is verified: a calm row under the title bar (the app works meanwhile). */
export function EmailBanner() {
  const { t } = useT();
  const me = useData((d) => d.me);
  const [busy, setBusy] = useState(false);
  if (!me?.email || me.verified) return null;
  const resend = async () => {
    setBusy(true);
    try {
      await api.me.resendEmail();
      toast(t('email.resent', { email: maskEmail(me.email!) }), { icon: 'mail', tone: 'ok' });
    } catch (e) {
      toast(errorText(e, tr().t), { icon: 'alert', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ns-email-bar" role="status">
      <Icon name="mail" />
      <div className="ns-email-bar-t">
        <b>{t('email.banner.title')}</b>
        <span>{t('email.banner.body', { email: maskEmail(me.email) })}</span>
      </div>
      <div className="ns-email-bar-acts">
        <Btn size="sm" kind="primary" onClick={() => openEmailDialog('verify')}>
          {t('email.banner.enter')}
        </Btn>
        <Btn size="sm" busy={busy} onClick={() => void resend()}>
          {t('email.resend')}
        </Btn>
        {/* not while the email is locked after a recovery (Settings says until when) */}
        {emailLockedUntil(me) ? null : (
          <Btn size="sm" kind="quiet" onClick={() => openEmailDialog('change')}>
            {t('email.change')}
          </Btn>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ "Add your email" ------------------------------ */

/**
 * An account made before email sign-in has no email: this step can't be
 * skipped (only signing out leaves it). After the address, the code; that
 * part may wait ("Later"), and the banner keeps reminding. For 7 days after
 * an account recovery no email can be added: the step says until when, and
 * lets them go on (signing in by username meanwhile).
 */
export function AddEmailGate() {
  const { t, fmt } = useT();
  const me = useData((d) => d.me);
  const [step, setStep] = useState<'add' | 'code' | null>('add');
  if (!me) return null;
  const show = me.email === null || (step === 'code' && !me.verified);
  if (!show || step === null) return null;
  const locked = emailLockedUntil(me);
  // another account confirmed this account's unverified address first: say so, not the legacy text
  const released = me.email === null && !!me.nx_email_released;
  return (
    <Dialog
      title={released ? t('email.add.releasedTitle') : t('email.add.title')}
      icon="mail"
      tone="mode"
      dismissable={false}
      onClose={() => {}}
      width={460}
      cls="ns-add-email"
    >
      {me.email === null && locked ? (
        <div className="ns-form ns-email-form">
          <p className="ns-help" role="status">
            {t('email.add.locked', { date: fmt.date(locked) })}
          </p>
          <div className="ns-row-btns end">
            <Btn size="sm" kind="quiet" icon="power" onClick={() => void signOut()}>
              {t('ns.signout')}
            </Btn>
            <Btn kind="primary" size="sm" onClick={() => setStep(null)} data-autofocus>
              {t('email.add.continue')}
            </Btn>
          </div>
        </div>
      ) : me.email === null ? (
        <>
          <ChangeEmailForm
            intro={
              released
                ? t('email.add.releasedBody', { username: me.username })
                : t('email.add.body')
            }
            askPassword={false}
            submitLabel={t('email.add.submit')}
            note={t('email.add.after')}
            onSent={() => setStep('code')}
          />
          <div className="ns-row-btns">
            <Btn size="sm" kind="quiet" icon="power" onClick={() => void signOut()}>
              {t('ns.signout')}
            </Btn>
          </div>
        </>
      ) : (
        <VerifyCodeForm
          onDone={(u) => (doneToast(me, u), setStep(null))}
          later={() => setStep(null)}
          onChange={() => {
            openEmailDialog('change');
            setStep(null);
          }}
        />
      )}
    </Dialog>
  );
}

/* ------------------------------ Settings › Security ------------------------------ */

export function EmailSettings() {
  const { t, fmt } = useT();
  const me = useData((d) => d.me);
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  // After a recovery the email can't change for 7 days: say until when, and
  // don't offer a change the server would refuse.
  const locked = emailLockedUntil(me);
  const cancel = async () => {
    setBusy(true);
    try {
      await api.me.cancelPendingEmail();
      updateData((d) => (d.me ? { ...d, me: { ...d.me, nx_pending_email: null } } : d));
      toast(t('email.cancelled', { email: maskEmail(me.email ?? '') }), { icon: 'check' });
    } catch (e) {
      toast(errorText(e, tr().t), { icon: 'alert', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <SetGroup title={t('email.title')}>
      <SetRow
        label={t('email.title')}
        sub={locked ? t('ns.err.100010.until', { date: fmt.date(locked) }) : t('email.sub')}
      >
        {me.email ? (
          <div className="ns-email-ctl">
            <MaskedEmail email={me.email} />
            <span className="nx-chip" data-tone={me.verified ? 'ok' : 'warn'}>
              {t(me.verified ? 'email.verified' : 'email.unverified')}
            </span>
            {!me.verified ? (
              <Btn size="sm" onClick={() => openEmailDialog('verify')}>
                {t('email.banner.enter')}
              </Btn>
            ) : null}
            <Btn
              size="sm"
              icon="edit"
              disabled={!!locked}
              onClick={() => openEmailDialog('change')}
            >
              {t('email.change')}
            </Btn>
          </div>
        ) : (
          <div className="ns-email-ctl">
            <span className="ns-muted">{t('email.none')}</span>
            <Btn
              size="sm"
              kind="primary"
              disabled={!!locked}
              onClick={() => openEmailDialog('change')}
            >
              {t('email.add')}
            </Btn>
          </div>
        )}
      </SetRow>
      {me.nx_pending_email ? (
        <SetRow
          label={t('email.pending', { email: maskEmail(me.nx_pending_email) })}
          sub={t('email.pendingSub')}
        >
          <div className="ns-email-ctl">
            <Btn size="sm" kind="primary" onClick={() => openEmailDialog('verify')}>
              {t('email.banner.enter')}
            </Btn>
            <Btn size="sm" kind="quiet" busy={busy} onClick={() => void cancel()}>
              {t('email.cancelChange')}
            </Btn>
          </div>
        </SetRow>
      ) : null}
    </SetGroup>
  );
}
